import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import api from "../../utils/api";
import toastMessage from "../../utils/toastMessage";
import ReusableDashboard from "../Dashboard/ReusableDashboard";
import language from "../../language";
import { statusFilter } from "./shipping/ShippingDashboard";
import { fetchShippingInQueueData } from "../../utils/queries/request/shipping-queue";
import { Modal } from "../Modal";
import { Table } from "../Table";
import { generateCSVData } from "../../utils/formatDataCSV";
import { downloadCSV } from "../../utils/csvDownloader";
import exportPDF from "../../utils/exportPDF";
import { defaultChartColors } from "../Dashboard/DashboardChart";

export const capitalizeFirstChar = (value = "") => {
  if (typeof value !== "string" || value.length === 0) {
    return "";
  }

  const spacedValue = value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");

  const capitalizedFirst =
    spacedValue.charAt(0).toUpperCase() + spacedValue.slice(1);

  return capitalizedFirst;
};

export const fmtDate = (iso) => moment(iso).format("DD MMM, YYYY");

export const sortDaysOfWeek = (days) => {
  const weekOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const normalize = (day) => day.toLowerCase();

  return days
    .map(normalize)
    .sort((a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b));
};

export const convertDataObjectToArray = (data) => {
  return Object.entries(data).map(([key, value]) => ({
    period: key,
    ...value,
  }));
};

export const getUniqueProductSums = (data) => {
  const result = {};

  Object.values(data).forEach((dayObj) => {
    Object.entries(dayObj).forEach(([key, value]) => {
      const count = parseInt(value) || 0;
      if (!result[key]) result[key] = 0;
      result[key] += count;
    });
  });

  const entries = Object.entries(result).map(([label, value]) => ({
    title: capitalizeFirstChar(label),
    count: value,
  }));

  entries.sort((a, b) => {
    if (a.title === "Total") return -1;
    if (b.title === "Total") return 1;
    return 0;
  });

  return entries;
};

export const getProductSumsFromArray = (dataArray) => {
  const totals = {};

  dataArray.forEach(({ productTypeName, count }) => {
    if (!totals[productTypeName]) totals[productTypeName] = 0;
    totals[productTypeName] += count;
  });

  return Object.entries(totals).map(([label, value]) => ({
    title: capitalizeFirstChar(label),
    count: value,
  }));
};

export const formatPeriodLabel = (periodStr) => {
  if (!periodStr) return "";

  if (periodStr.includes("W")) {
    // Weekly: "2025-W18 2025-04-28-2025-05-04"
    const [weekPart, rangePart] = periodStr.split(" ");
    const [start, end] = rangePart.split("-");
    const format = (d) => moment(d).format("DD MMM, YYYY");
    return `${weekPart} (${format(start)} - ${format(end)})`;
  } else {
    // Daily: "Sun, May 18, 2025"
    return moment(periodStr).format("ddd, MMM D, YYYY");
  }
};

export const getCardItems = (data) => {
  if (Array.isArray(data)) {
    return getProductSumsFromArray(data || {});
  } else {
    return getUniqueProductSums(data || {});
    //   return sortDaysOfWeek(
    //     Object.keys(this.state.productTypesRequestedCounts || {})
    //   ).map((key) => ({
    //     title: capitalizeFirstChar(key),
    //     count: this.state.productTypesRequestedCounts[key]?.total || 0,
    //   }));
  }
};

const formatNumber = (value) => {
  if (value == null || isNaN(value)) return 0;
  const num = Number(value);
  return Math.round(num * 100) / 100; // two decimals max
};

class RequestDashboard extends React.Component {
  state = {
    isLoading: false,
    dailyLoading: false,
    weekdayLoading: false,
    monthlyLoading: false,
    nominativeLoading: false,
    error: {},
    data: {},
    requestsStatusCounts: {},
    filter: {
      startDate: moment().subtract(1, "months").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      periodType: { value: "weekday", label: "Week day" },
      centers: [],
      centerSites: [],
      status: { value: "pending", label: "Pending" },
    },
    page: 1,
    limit: 10,
  };

  componentDidMount = async () => {
    await this.fetchData();
  };

  onFilterChange(field, value) {
    const _filters = this.state.filter;
    if (field === "date-range") {
      _filters.startDate = value.startDate;
      _filters.endDate = value.endDate;
    } else {
      _filters[field] = value;
    }

    this.setState({
      filter: { ..._filters },
    });
  }

  fetchData = async () => {
    this.setState({
      isLoading: false,
      dailyLoading: true,
      weekdayLoading: true,
      monthlyLoading: true,
    });

    const startDate =
      this.state.filter?.startDate ||
      moment().subtract(1, "months").format("YYYY-MM-DD");
    const endDate = this.state.filter?.endDate || moment().format("YYYY-MM-DD");
    const centers = (this.state.filter?.centers || []).map(
      ({ value }) => value
    );
    const centerSites = (this.state.filter?.centerSites || []).map(
      ({ value }) => value
    );
    const status = this.state?.filter?.status?.value || "pending";

    const base = {
      method: "GET",
      url: `/product-request/product-types-requested-counts`,
      params: { startDate, endDate, centers, centerSites, status },
    };

    const requestId = Date.now();
    this.latestRequestFetchId = requestId;

    const buildReq = (periodKey, baseUrl = base) =>
      api({ ...baseUrl, params: { ...baseUrl.params, periodType: periodKey } })
        .then(({ data }) => {
          if (this.latestRequestFetchId !== requestId) return;
          this.setState((prev) => ({
            data: { ...(prev.data || {}), [periodKey]: data },
            [`${periodKey}Loading`]: false,
          }));
        })
        .catch((err) => {
          if (this.latestRequestFetchId !== requestId) return;
          console.error(
            `${periodKey} product-types-requested-counts error`,
            err
          );
          toastMessage("error", err);
          this.setState({ [`${periodKey}Loading`]: false });
        });

    const dailyReq = buildReq("daily");
    const weekdayReq = buildReq("weekday");
    const monthlyReq = buildReq("monthly");
    const nominativeBase = {
      method: "GET",
      url: `/bloodrequest/nominative-summary-report`,
      params: { startDate, endDate },
    };
    const nominativeReq = buildReq("nominative", nominativeBase);

    await Promise.allSettled([dailyReq, weekdayReq, monthlyReq, nominativeReq]);

    if (this.latestRequestFetchId === requestId) {
      this.setState({ isLoading: false });
    }
  };

  handleCounts(data) {
    const countsPerProductType = {};
    for (let i = 0; i < data.length; i++) {
      if (data[i].productTypeName) {
        if (!countsPerProductType[data[i].productTypeName]) {
          countsPerProductType[data[i].productTypeName] = {
            ...data[i],
            total: 0,
          };
        }

        countsPerProductType[data[i].productTypeName].total += data[i].count;
      }
    }
    return countsPerProductType;
  }

  generateChartData = (raw) => {
    // Return null if input is empty or invalid
    if (
      !raw ||
      (Array.isArray(raw) && raw.length === 0) ||
      (typeof raw === "object" && Object.keys(raw).length === 0)
    ) {
      return null;
    }

    const isArrayFormat = Array.isArray(raw);
    let days = [];
    const allBloodTypes = new Set();

    // Process data based on input format
    if (isArrayFormat) {
      // Filter out invalid entries and collect unique days/types
      const validEntries = raw.filter(
        (item) =>
          item &&
          item.period &&
          item.productTypeName &&
          typeof item.count !== "undefined"
      );

      if (validEntries.length === 0) return null;

      // Get sorted unique days
      days = [...new Set(validEntries.map((item) => item.period))].sort(
        (a, b) => new Date(a) - new Date(b)
      );

      // Collect all blood types
      validEntries.forEach((item) => {
        if (item.productTypeName) {
          allBloodTypes.add(item.productTypeName);
        }
      });
    } else {
      // Original grouped-by-day format
      days = sortDaysOfWeek(Object.keys(raw));
      if (!days || days.length === 0) return null;

      // Collect all blood types
      days.forEach((day) => {
        const dayData = raw[day];
        if (dayData && typeof dayData === "object") {
          Object.keys(dayData).forEach((key) => {
            if (key !== "total") {
              allBloodTypes.add(key);
            }
          });
        }
      });
    }

    // Return null if no valid blood types found
    if (allBloodTypes.size === 0) return null;

    const bloodTypes = Array.from(allBloodTypes).filter(Boolean);

    // Build chart data
    const chartData = [["Day", ...bloodTypes]];

    days.forEach((day) => {
      const row = [isArrayFormat ? day : capitalizeFirstChar(day)];

      bloodTypes.forEach((bt) => {
        if (isArrayFormat) {
          const entry = raw.find(
            (item) => item.period === day && item.productTypeName === bt
          );
          row.push(entry ? parseInt(entry.count) || 0 : 0);
        } else {
          const dayData = raw[day] || {};
          row.push(parseInt(dayData[bt]) || 0);
        }
      });

      chartData.push(row);
    });

    return chartData;
  };

  handleShowModal(modal, selectedData) {
    this.setState(
      {
        [modal]: true,
        selectedData,
      },
      () => {
        if (modal === "showModal") {
          this.getDataByProductType(true, {
            productType: [selectedData.productType],
          });
        }
      }
    );
  }

  getDataByProductType = async (isLoading, search = {}) => {
    try {
      this.setState({ isLoadingDataPerProductType: isLoading });

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      const options = {
        method: "GET",
        url: `/product-request`,
        params: {
          startDate,
          endDate,
          centers,
          centerSites,
          status: this.state?.status || "received",
          limit: this.state.limit,
          page: this.state.page,
          ...search,
        },
      };

      const { data } = await api(options);

      this.setState({
        producttypeData: data.data,
        totalPageCount: data.count,
        isLoadingDataPerProductType: false,
      });
    } catch (error) {
      this.setState({
        isLoadingDataPerProductType: false,
      });
      toastMessage("error", error);
    }
  };

  handlePagination(page, _tab, limit) {
    this.setState(
      {
        page,
        ...(limit != null && { limit }),
      },
      () => {
        this.getDataByProductType(true, {
          productType: [this.state.selectedData.productType],
        });
      }
    );
  }

  handleRemoteSearch = async (search) => {
    await new Promise((r) => this.setState({ searching: true, page: 1 }, r));
    await this.getDataByProductType(true, {
      productType: [this.state.selectedData.productType],
      donationNumber: search || search !== "" ? search : undefined,
    });

    this.setState({
      searching: false,
    });
  };

  handleSearch = async (e) => {
    const search = e?.target?.value;
    this.setState({ search_text: search });
  };

  handleCloseModal(modal) {
    this.setState({
      [modal]: false,
    });
  }

  getDataByProductTypeExport = async (search = {}) => {
    try {
      const { defaultLanguage } = this.props;

      const startDate =
        this.state.filter?.startDate ||
        moment().subtract(1, "months").format("YYYY-MM-DD");
      const endDate =
        this.state.filter?.endDate || moment().format("YYYY-MM-DD");
      const centers = (this.state.filter?.centers || []).map(
        ({ value }) => value
      );
      const centerSites = (this.state.filter?.centerSites || []).map(
        ({ value }) => value
      );

      const { data } = await fetchShippingInQueueData(defaultLanguage, {
        ...search,
        page: 1,
        limit: 100000,
        startDate,
        endDate,
        centers,
        centerSites,
        status: this.state?.status,
      });

      return data;
    } catch (error) {
      toastMessage("error", error);
      return [];
    }
  };

  returnTableHeaders() {
    let headers = [
      {
        title: language[this.props.defaultLanguage].donation_number,
        key: "donationNumber",
      },
      {
        title: language[this.props.defaultLanguage].source,
        key: "source",
      },
      {
        title: language[this.props.defaultLanguage].destination,
        key: "destination",
      },
      {
        title: language[this.props.defaultLanguage].status,
        key: "status",
        type: "status",
      },
      {
        title: language[this.props.defaultLanguage].product_verified_shipped_by,
        key: "addedBy",
        styles: {
          textTransform: "uppercase",
        },
      },
      {
        title: language[this.props.defaultLanguage].date,
        key: "createdAt",
        formatTime: "lll",
        isMoment: true,
      },
    ];

    return headers;
  }

  downloadExcel = async () => {
    try {
      const data = await this.getDataByProductTypeExport({
        product: this.state.selectedData.productType,
      });

      const headers = this.returnTableHeaders();

      const csvData = generateCSVData(headers, data);

      downloadCSV(
        csvData,
        `Product requested: ${this.state.selectedData?.productTypeName}`
      );
    } catch (error) {
      console.error(error);
    }
  };

  downloadPDF = async () => {
    const headers = this.returnTableHeaders();

    const data = await this.getDataByProductTypeExport({
      product: this.state.selectedData.productType,
    });

    exportPDF(
      `Product requested: ${this.state.selectedData?.productTypeName}`,
      headers,
      data || []
    );
  };

  // Nominative helpers ---
  toCountChart = (
    items = [],
    { labelKey = "_id", headerLabel = "Label", labelTransform } = {}
  ) => {
    if (!Array.isArray(items) || items.length === 0) return null;
    const rows = items.map((it) => {
      const rawLabel = String(it?.[labelKey] ?? "");
      const label =
        typeof labelTransform === "function"
          ? labelTransform(rawLabel)
          : rawLabel;
      return [label, Number(it?.count ?? 0)];
    });
    return [[headerLabel, "Count"], ...rows];
  };

  discoverLabelKey = (items = []) => {
    const sample = items?.[0] ?? {};
    const keys = Object.keys(sample);
    const label = keys.find((k) => k !== "count" && k !== "_id");
    return label || "_id";
  };

  toUrgencyOverTimeChart = (series = []) => {
    if (!Array.isArray(series) || series.length === 0) return null;
    const rows = series
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((p) => [
        fmtDate(p.date), // << formatted with moment
        formatNumber(p.totalRequests || 0),
        formatNumber(p.urgentRequests || 0),
      ]);
    return [["Date", "Total Requests", "Urgent Requests"], ...rows];
  };

  toUrgentPctChart = (series = []) => {
    if (!Array.isArray(series) || series.length === 0) return null;
    const rows = series
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((p) => [fmtDate(p.date), formatNumber(p.urgentPercentage || 0)]); // << formatted
    return [["Date", "Urgent %"], ...rows];
  };

  summaryToCards = (summary = {}) => {
    const order = [
      "totalRequests",
      "urgentRequests",
      "urgentPercentage",
      "initiatedRequests",
      "pendingRequests",
      "inProgressRequests",
      "confirmedRequests",
      "completedRequests",
      "rejectedRequests",
      "cancelledRequests",
    ];
    return order
      .filter((k) => k in summary)
      .map((k) => {
        const val = summary[k];
        const isPct = String(k).toLowerCase().includes("percentage");
        return {
          title: `Nominative ${capitalizeFirstChar(k)}`, // clarify nominative scope
          count: isPct ? `${val}%` : Number(val || 0),
        };
      });
  };

  processingToCards = (pt = {}) => {
    if (!pt || typeof pt !== "object") return [];
    const map = [
      ["avgProcessingTime", "Avg Processing (h)"],
      ["minProcessingTime", "Min Processing (h)"],
      ["maxProcessingTime", "Max Processing (h)"],
    ];
    return map
      .filter(([k]) => k in pt)
      .map(([k, label]) => ({
        title: `Nominative ${label}`,
        count: formatNumber(pt[k] || 0),
      }));
  };

  buildNominativeWidgets = (nominativeData = {}) => {
    const widgets = { cardGroups: [], charts: [] };

    // Cards
    if (nominativeData.summary) {
      widgets.cardGroups.push({
        title: "Nominative — Summary",
        color: defaultChartColors[0],
        col: 3,
        isLoading: this.state.nominativeLoading,
        items: this.summaryToCards(nominativeData.summary),
      });
    }

    const ptCards = this.processingToCards(
      nominativeData?.trends?.processingTime
    );
    if (ptCards.length) {
      widgets.cardGroups.push({
        title: "Nominative — Processing Time",
        color: defaultChartColors[3],
        col: 3,
        isLoading: this.state.nominativeLoading,
        items: ptCards,
      });
    }

    // Charts
    const distributions = nominativeData.distributions || {};
    const distKeys = Object.keys(distributions);

    distKeys.forEach((key) => {
      const items = distributions[key] || [];
      if (!Array.isArray(items) || items.length === 0) return;

      if (key === "status") {
        const statusData = this.toCountChart(items, {
          labelKey: "_id",
          headerLabel: "Status",
          labelTransform: capitalizeFirstChar,
        });
        if (statusData) {
          widgets.charts.push({
            id: "nominative-status-pie",
            title: "Nominative Requests by Status",
            type: "PieChart",
            data: statusData,
            isLoading: this.state.nominativeLoading,
            height: "380px",
            col: 4,
            legend: "right",
          });
        }
      } else {
        const labelKey = this.discoverLabelKey(items);
        const header = capitalizeFirstChar(key);
        const chartData = this.toCountChart(items, {
          labelKey,
          headerLabel: header,
        });

        if (chartData) {
          const col = key.toLowerCase() === "hospital" ? 8 : 6;

          widgets.charts.push({
            id: `nominative-${key}-bars`,
            title: `Nominative Requests by ${capitalizeFirstChar(key)}`,
            type: "ColumnChart",
            data: chartData,
            isLoading: this.state.nominativeLoading,
            height: "380px",
            col,
          });
        }
      }
    });

    const urgencySeries = nominativeData?.trends?.urgencyOverTime || [];

    const totalsVsUrgent = this.toUrgencyOverTimeChart(urgencySeries);
    if (totalsVsUrgent) {
      widgets.charts.push({
        id: "nominative-urgency-trend",
        title: "Nominative — Urgency Over Time (Total vs Urgent)",
        type: "LineChart",
        data: totalsVsUrgent,
        isLoading: this.state.nominativeLoading,
        height: "400px",
        col: 6,
      });
    }

    const urgentPct = this.toUrgentPctChart(urgencySeries);
    if (urgentPct) {
      widgets.charts.push({
        id: "nominative-urgent-pct",
        title: "Nominative — Urgent Percentage Over Time",
        type: "LineChart",
        data: urgentPct,
        isLoading: this.state.nominativeLoading,
        height: "400px",
        col: 6,
        legend: "bottom",
      });
    }

    return widgets;
  };

  render() {
    const countsPerProductType = this.handleCounts(
      this.state.data.monthly || []
    );
    const nominative = this.state?.data?.nominative || {};
    const { cardGroups: nominativeCardGroups, charts: nominativeCharts } =
      this.buildNominativeWidgets(nominative);
    return (
      <>
        <ReusableDashboard
          loading={this.state.isLoading}
          filtersConfig={{
            hasCenters: true,
            hasSites: true,
            initialValues: this.state.filter,
            onFilterChange: (field, value) => this.onFilterChange(field, value),
            customFilter: [statusFilter],
          }}
          // filters={this.state.filter}
          onFind={() => this.fetchData()}
          cardGroups={[
            {
              title: "Product Requested",
              color: defaultChartColors[0],
              col: 3,
              isLoading: this.state.monthlyLoading,
              items: Object.keys(countsPerProductType || {}).map((key) => ({
                title: capitalizeFirstChar(key),
                count: countsPerProductType[key].total || 0,
                onPress: () =>
                  this.handleShowModal("showModal", countsPerProductType[key]),
              })),
            },
            ...nominativeCardGroups,
          ]}
          charts={[
            {
              id: "product-request-daily",
              title: `Daily Product Requested`,
              type: "ColumnChart",
              data: this.generateChartData(this.state.data.daily),
              isLoading: this.state.dailyLoading,
              height: "400px",
              col: 6,
            },
            {
              id: "product-request-weekday",
              title: `Week days Product Requested`,
              type: "ColumnChart",
              data: this.generateChartData(this.state.data.weekday),
              isLoading: this.state.weekdayLoading,
              height: "400px",
              col: 6,
            },
            {
              id: "product-request-monthly",
              title: `Monthly Product Requested`,
              type: "ColumnChart",
              data: this.generateChartData(this.state.data.monthly),
              isLoading: this.state.monthlyLoading,
              height: "400px",
              col: 12,
            },
            ...nominativeCharts,
          ]}
        />

        <Modal
          show={this.state.showModal}
          handleClose={() => this.handleCloseModal("showModal")}
          title={this.state.selectedData?.productTypeName}
          size="lg"
        >
          <Table
            data={this.state.producttypeData}
            isSearch
            page={this.state.page}
            limit={this.state.limit}
            addSearchButton
            search_text={this.state.search_text}
            searching={this.state.searching}
            handleSearch={(e) => this.onChangeText("search_text", e)}
            handlePressSearch={() =>
              this.handleRemoteSearch(this.state.search_text)
            }
            totalPageCount={this.state.totalPageCount}
            totalRecords={this.state.totalRecords}
            isLoading={this.state.isLoadingDataPerProductType}
            handlePagination={this.handlePagination.bind(this)}
            headers={this.returnTableHeaders()}
            filters={[
              {
                type: "export",
                title: language[this.props.defaultLanguage].export_,
                button_type: "dropdown",
                icon: "download",
                options: [
                  {
                    name: "PDF",
                    onPress: this.downloadPDF.bind(this),
                  },
                  {
                    name: "CSV",
                    onPress: this.downloadExcel.bind(this),
                  },
                ],
              },
            ]}
          />
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  const { filters } = state.Filters;
  return {
    defaultLanguage,
    filters,
  };
};

export default connect(mapStateToProps)(RequestDashboard);
